import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { CommonPageProps, NonUndefined } from '@/types';
import DefaultLayout from '@/components/layout/DefaultLayout';
import { getCommonPageProps } from '@/api/common';
import Image from 'next/image';
import Button from '@/components/shared/Button';
import { useRouter } from 'next/router';
import { CATEGORIES_SLUGS } from '@/api/categories';
import dynamic from 'next/dynamic';
import { ApiCompareOrViewedResponse, getCompareOrViewed } from '@/api/compare-or-viewed';

type Cookies = Partial<{
    [key: string]: string | string[];
}>;

const parseProductIds = (cookies: Cookies, keys: string[]): number[] => {
    return keys.reduce<number[]>((allIds, key) => {
        const cookieValue = cookies[key];
        if (cookieValue) {
            if (typeof cookieValue === 'string') {
                try {
                    const parsedArray = JSON.parse(cookieValue);
                    return allIds.concat(parsedArray);
                } catch (e) {
                    return allIds;
                }
            } else if (Array.isArray(cookieValue)) {
                const ids = cookieValue.reduce<number[]>((idsArray, jsonArray) => {
                    try {
                        const parsedArray = JSON.parse(jsonArray);
                        return idsArray.concat(parsedArray);
                    } catch (e) {
                        return idsArray;
                    }
                }, []);
                return allIds.concat(ids);
            }
        }
        return allIds;
    }, []);
};

export const RECENTS_COOKIES_NAME = 'recents';

const ViewedTabs = dynamic(() => import('@/components/general/ViewedTabs'));

const ViewedPage = ({ h1, productsResponse, categories }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const router = useRouter();

    return (
        <DefaultLayout>
            <div className="wrapper catalog-wrapper">
                <div className="catalog-top">
                    <div className="catalog-top__main">
                        <Button
                            icon={<Image src="/img/back.svg" alt="" unoptimized fill />}
                            iconReverse
                            onClick={() => {
                                window.history.length > 1 ? router.back() : router.push(`/catalog`);
                            }}
                            className="back-button-clickable"
                            geometryVariant="arrow"
                        >
                            На главную
                        </Button>
                    </div>
                </div>
                <ViewedTabs initialResponse={productsResponse} categories={categories} title={h1} />
            </div>
        </DefaultLayout>
    );
};

export default ViewedPage;

type ViewedPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        productsResponse: ApiCompareOrViewedResponse;
    }
>;

export const getServerSideProps: GetServerSideProps<ViewedPageProps> = async ({ req }) => {
    const keys = Object.keys(CATEGORIES_SLUGS).map((item) => `${RECENTS_COOKIES_NAME}-${item}`);
    const { cookies } = req;
    const productIds = parseProductIds(cookies, keys);
    const [commonPageProps, products] = await Promise.all([
        getCommonPageProps(),
        getCompareOrViewed({
            params: {
                limit: 100,
                ids: productIds,
            },
        }),
    ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'viewed-page',
            meta: {
                ...commonPageProps.meta,
                title: 'Просмотренные товары',
            },
            h1: 'Просмотренные товары',
            productsResponse: products,
            categories: commonPageProps.categories,
        } satisfies ViewedPageProps,
    };
};
